import { Icon } from './ui/Icon';
import type { ProductSummary } from '../types';
import { t } from '../i18n/t';

/**
 * Cards cycle through the three brand accents so a grid reads as colourful
 * without introducing hues outside the palette. Text tones are darkened to
 * hold a 4.5:1 contrast ratio on white — notably for the yellow accent.
 */
const CARD_ACCENTS = [
  {
    block: 'bg-secondary-500/12 text-secondary-600',
    bar: 'bg-secondary-500',
    cta: 'text-secondary-700',
  },
  {
    block: 'bg-accent-500/12 text-accent-600',
    bar: 'bg-accent-500',
    cta: 'text-accent-700',
  },
  {
    block: 'bg-warning-500/20 text-warning-700',
    bar: 'bg-warning-500',
    cta: 'text-warning-700',
  },
] as const;

export interface InsuranceCardProps {
  product: ProductSummary;
  accentIndex?: number;
}

export function InsuranceCard({ product, accentIndex = 0 }: InsuranceCardProps) {
  const accent = CARD_ACCENTS[accentIndex % CARD_ACCENTS.length];

  return (
    <li className="h-full">
      <a
        href={product.path}
        className="group flex h-full flex-col border border-slate-200 bg-white transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_18px_40px_-24px_rgba(44,62,80,0.35)] motion-reduce:hover:translate-y-0"
      >
        {/* Colour block stands in for the lifestyle imagery this layout expects */}
        <span
          className={`flex h-32 items-center justify-center ${accent.block}`}
          aria-hidden="true"
        >
          <Icon name={product.icon} className="h-11 w-11" strokeWidth={1.5} />
        </span>
        <span className={`h-1 w-full ${accent.bar}`} aria-hidden="true" />

        <div className="flex flex-1 flex-col p-6 sm:p-7">
          <h3 className="font-display text-lg font-extrabold uppercase leading-tight tracking-[-0.01em] text-primary-500">
            {product.name}
          </h3>
          <p className="mt-3 flex-1 text-sm font-light leading-[1.65] tracking-normal text-slate-600">
            {product.summary}
          </p>

          <span
            className={`mt-6 inline-flex items-center gap-2 text-[0.8125rem] font-semibold uppercase tracking-[0.08em] ${accent.cta}`}
          >
            {t('ui.cta.learn_more')}
            <Icon
              name="arrow-right"
              className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1 motion-reduce:transition-none"
              strokeWidth={2}
            />
          </span>
        </div>
      </a>
    </li>
  );
}

export interface InsuranceCardGridProps {
  products: ProductSummary[];
  /** Two columns suit the narrower right-hand rail of a split layout. */
  columns?: 2 | 3;
}

export function InsuranceCardGrid({ products, columns = 3 }: InsuranceCardGridProps) {
  const columnClass = columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <ul className={`grid gap-6 ${columnClass}`}>
      {products.map((product, index) => (
        <InsuranceCard key={product.slug} product={product} accentIndex={index} />
      ))}
    </ul>
  );
}
